/**
 * API Service with CSRF Token Protection
 * 
 * This service manages all API communications with automatic CSRF token handling.
 * It uses axios interceptors to automatically include CSRF tokens in requests
 * and retry failed requests with fresh tokens.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '../config/constants';
import { errorTracker } from './monitoring';

// ============================================================================
// TYPES
// ============================================================================

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface SubscribeData {
  email: string;
}

interface ApiResponse<T = any> {
  message: string;
  data?: T;
}

interface CsrfTokenResponse {
  csrfToken: string;
}

// ============================================================================
// CSRF TOKEN MANAGER
// ============================================================================

class CSRFTokenManager {
  private token: string | null = null;
  private fetchPromise: Promise<string> | null = null;

  /**
   * Fetches a new CSRF token from the server
   * Prevents multiple simultaneous fetch requests
   */
  async fetchToken(): Promise<string> {
    // Return existing fetch promise if one is in progress
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Create new fetch promise
    console.log('🔄 Fetching new CSRF token from server...');
    this.fetchPromise = axios
      .get<CsrfTokenResponse>(`${API_BASE_URL}/csrf-token`, {
        withCredentials: true,
      })
      .then((response) => {
        this.token = response.data.csrfToken;
        this.fetchPromise = null;
        
        // DEBUG: Log token and cookie info
        console.log('✅ CSRF Token fetched successfully:', {
          token: this.token?.substring(0, 20) + '...',
          fullLength: this.token?.length,
          cookies: document.cookie,
          hasCookie: document.cookie.includes('bioskar.sid') || document.cookie.includes('connect.sid')
        });
        
        return this.token;
      })
      .catch((error) => {
        this.fetchPromise = null;
        console.error('❌ Failed to fetch CSRF token:', error);
        errorTracker.logError(error, {
          tags: { service: 'csrf', action: 'fetch_token' },
        });
        throw new Error('Unable to fetch CSRF token');
      });

    return this.fetchPromise;
  }

  /**
   * Gets the cached token or fetches a new one if not available
   */
  async getToken(): Promise<string> {
    if (this.token) {
      console.log('✅ Using cached CSRF token');
      return this.token;
    }
    console.log('⚠️ No cached token, fetching new one...');
    return this.fetchToken();
  }

  /**
   * Clears the cached token (used when token expires)
   */
  clearToken(): void {
    this.token = null;
  }

  /**
   * Pre-fetches token on application load
   */
  async initialize(): Promise<void> {
    try {
      await this.fetchToken();
      console.log('CSRF token initialized successfully');
    } catch (error) {
      console.error('Failed to initialize CSRF token:', error);
    }
  }
}

// Create singleton instance
const csrfManager = new CSRFTokenManager();

// ============================================================================
// AXIOS INSTANCE CONFIGURATION
// ============================================================================

/**
 * Configured axios instance with CSRF protection
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: true, // CRITICAL: Enables cookies for session management
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// REQUEST INTERCEPTOR - Add CSRF Token
// ============================================================================

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Only add CSRF token for POST, PUT, PATCH, DELETE requests
    const methodsRequiringToken = ['post', 'put', 'patch', 'delete'];
    
    if (config.method && methodsRequiringToken.includes(config.method.toLowerCase())) {
      try {
        const token = await csrfManager.getToken();
        
        // DEBUG: Log token info
        console.log('🔑 CSRF Token Info:', {
          token: token?.substring(0, 20) + '...',
          url: config.url,
          method: config.method,
          withCredentials: config.withCredentials
        });
        
        // Add token to headers (support both formats for compatibility)
        config.headers = config.headers || {};
        config.headers['CSRF-Token'] = token;
        config.headers['X-CSRF-Token'] = token;
      } catch (error) {
        console.error('❌ Failed to add CSRF token to request:', error);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR - Handle Token Expiration
// ============================================================================

apiClient.interceptors.response.use(
  (response) => response, // Success - pass through
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 403 error and haven't retried yet, refresh token and retry
    if (error.response?.status === 403 && !originalRequest._retry) {
      console.log('⚠️ Got 403 error, attempting token refresh and retry...');
      originalRequest._retry = true;

      try {
        // Clear old token and fetch new one
        csrfManager.clearToken();
        const newToken = await csrfManager.fetchToken();

        console.log('🔄 Retrying request with new token...');
        
        // Update request headers with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['CSRF-Token'] = newToken;
        originalRequest.headers['X-CSRF-Token'] = newToken;

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('❌ Failed to refresh CSRF token:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    console.error('❌ Request failed:', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method
    });

    return Promise.reject(error);
  }
);

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Initialize CSRF protection
 * Call this once when the application loads
 */
export const initializeCSRF = async (): Promise<void> => {
  await csrfManager.initialize();
};

/**
 * Send contact form submission
 */
export const sendContactForm = async (data: ContactFormData): Promise<string> => {
  try {
    const response = await apiClient.post<ApiResponse>('/contact', data);
    return response.data.message || 'Message sent successfully';
  } catch (error: any) {
    // Log error to monitoring
    errorTracker.logError(error, {
      tags: { service: 'contact', action: 'submit_form' },
      extra: { formData: { name: data.name, email: data.email, subject: data.subject } },
    });

    // Handle specific error cases
    if (error.response?.status === 403) {
      throw new Error('Security validation failed. Please refresh and try again.');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid form data');
    }
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. Please try again.');
    }
    if (!error.response) {
      throw new Error('Network error. Please check your connection.');
    }
    throw new Error(error.response?.data?.message || 'Failed to send message');
  }
};

/**
 * Subscribe to newsletter
 */
export const subscribeToNewsletter = async (data: SubscribeData): Promise<string> => {
  try {
    const response = await apiClient.post<ApiResponse>('/subscribe', data);
    return response.data.message || 'Successfully subscribed!';
  } catch (error: any) {
    // Log error to monitoring
    errorTracker.logError(error, {
      tags: { service: 'newsletter', action: 'subscribe' },
      extra: { email: data.email },
    });

    // Handle specific error cases
    if (error.response?.status === 403) {
      throw new Error('Security validation failed. Please refresh and try again.');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid email address');
    }
    if (error.response?.status === 409) {
      throw new Error('This email is already subscribed');
    }
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. Please try again.');
    }
    if (!error.response) {
      throw new Error('Network error. Please check your connection.');
    }
    throw new Error(error.response?.data?.message || 'Failed to subscribe');
  }
};

/**
 * Generic API error handler (optional utility)
 */
export const handleApiError = (error: any): Error => {
  if (error.response) {
    // Server responded with error
    return new Error(error.response.data?.message || 'An error occurred');
  } else if (error.request) {
    // Request made but no response
    return new Error('No response from server');
  } else {
    // Something else happened
    return new Error(error.message || 'An unexpected error occurred');
  }
};

// Export CSRF manager for advanced use cases (optional)
export { csrfManager };
